import {
  type DefaultContext,
  type DocumentNode,
  type FetchPolicy,
  useQuery,
} from "@apollo/client";
import { Button, ControlGroup, MenuItem } from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import { type ItemCreateDrawerProps } from "@package/ui-apollo-components";
import {
  type ComponentType,
  type PropsWithChildren,
  useMemo,
  useState,
} from "react";
import { ItemSelectStateProvider } from "./item-select-context-provider";
import {
  getDisplayForDocumentItem,
  type ItemSelectQuery,
  type ItemSelectQueryVariables,
} from "./item-select-utils";
import { ItemSelectButton } from "./ItemSelectButton";

type ItemSelectProps<
  Q extends ItemSelectQuery,
  V extends ItemSelectQueryVariables,
> = {
  document: DocumentNode;
  context?: DefaultContext;
  createDrawerElement?: ComponentType<ItemCreateDrawerProps>;
  variables?: V;
  sorted?: boolean;
  query?: Q;
  fetchPolicy?: FetchPolicy;
  activeItemId?: string | null;
  disabled?: boolean;
  filter?: (item?: Q["items"][0]) => boolean;
  filterable?: boolean;
  nullable?: boolean;
  nullableText?: string;
  onItemSelected?: (id?: string | null, item?: Q["items"][0]) => void;
};

export function ItemSelect<
  Q extends ItemSelectQuery,
  V extends ItemSelectQueryVariables,
>({
  activeItemId,
  children,
  context,
  createDrawerElement: CreateDrawerElement,
  disabled,
  document,
  fetchPolicy,
  filter,
  filterable = true,
  nullable,
  nullableText,
  sorted = true,
  variables,
  onItemSelected,
}: PropsWithChildren<ItemSelectProps<Q, V>>) {
  const { data, loading, error } = useQuery<Q, ItemSelectQueryVariables>(
    document,
    {
      fetchPolicy,
      context,
      variables: {
        page: 0,
        perPage: 1000,
        ...variables,
      },
    },
  );
  const items = useMemo(() => {
    let dataItems = data?.items?.slice() ?? [];

    if (filter) {
      dataItems = dataItems.filter(filter) as Q["items"];
    }

    if (sorted) {
      dataItems.sort((a, b) => {
        if (!a && b) {
          return 1;
        }
        if (a && !b) {
          return -1;
        }
        return (
          getDisplayForDocumentItem<Q>(a)?.localeCompare(
            getDisplayForDocumentItem<Q>(b) ?? "",
            ["en-US"],
            { caseFirst: "lower" },
          ) ?? 0
        );
      });
    }

    if (nullable) {
      dataItems.splice(0, 0, {
        id: null,
      });
    }

    return dataItems;
  }, [data?.items, filter, nullable, sorted]);

  const [isOpenCreateDrawer, setIsOpenCreateDrawer] = useState<boolean>(false);

  return (
    <ControlGroup>
      <Select<Q["items"][0]>
        activeItem={data?.items?.find((item) => item.id === activeItemId)}
        disabled={disabled || loading}
        fill
        filterable={filterable}
        items={items}
        itemPredicate={(query, item) =>
          getDisplayForDocumentItem<Q>(item, nullableText)
            ?.toLocaleLowerCase()
            ?.includes(query.toLocaleLowerCase()) ?? false
        }
        itemRenderer={(item, { handleClick, handleFocus, modifiers }) => (
          <MenuItem
            active={modifiers.active}
            disabled={modifiers.disabled}
            key={item.id}
            onClick={handleClick}
            onFocus={handleFocus}
            roleStructure="listoption"
            text={getDisplayForDocumentItem<Q>(item, nullableText)}
          />
        )}
        noResults={
          <MenuItem
            disabled={true}
            text={
              error
                ? "Failed loading items"
                : loading
                  ? "Loading..."
                  : "No Items"
            }
            roleStructure="listoption"
          />
        }
        onItemSelect={(item) => {
          onItemSelected?.(item?.id, item);
        }}
        popoverProps={{
          minimal: true,
        }}
      >
        <ItemSelectStateProvider
          activeItemId={activeItemId}
          disabled={disabled}
          items={items}
        >
          {children ?? <ItemSelectButton noSelectionText={nullableText} />}
        </ItemSelectStateProvider>
      </Select>
      {CreateDrawerElement && (
        <ControlGroup>
          <Button
            icon="add"
            intent="primary"
            minimal
            outlined
            onClick={() => setIsOpenCreateDrawer(true)}
          />
        </ControlGroup>
      )}
      {CreateDrawerElement && (
        <CreateDrawerElement
          isOpen={isOpenCreateDrawer}
          onClose={() => setIsOpenCreateDrawer(false)}
          onCreated={(id) => {
            onItemSelected?.(id);
          }}
        />
      )}
    </ControlGroup>
  );
}
